import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Certificate } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import { doc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../hooks/useToast';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PlusIcon: React.FC<{className?: string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>);
const MenuIcon: React.FC<{ className?: string }> = ({ className }) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>);

const SortableCertificateItem: React.FC<{ certificate: Certificate, onDelete: (certificate: Certificate) => void }> = ({ certificate, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: certificate.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <tr ref={setNodeRef} style={style}>
            <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm w-12">
                <button {...attributes} {...listeners} className="cursor-grab p-2 text-gray-400 hover:text-gray-700">
                    <MenuIcon className="h-5 w-5" />
                </button>
            </td>
            <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                <img src={certificate.imageUrl} alt={certificate.title} className="w-24 h-16 object-contain bg-gray-50 p-1" />
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-900 whitespace-no-wrap font-semibold">{certificate.title}</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-900 whitespace-no-wrap">{certificate.issuingBody}</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-900 whitespace-no-wrap">{certificate.year}</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-right whitespace-no-wrap">
                <Link to={`/admin/certificate/edit/${certificate.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">Edit</Link>
                <button onClick={() => onDelete(certificate)} className="text-red-600 hover:text-red-900">Delete</button>
            </td>
        </tr>
    );
};

const AdminCertificatesPage: React.FC = () => {
    const { data: certificates, setData: setCertificates, isLoading } = useCollection<Certificate>('certificates', { field: 'order', direction: 'asc' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Certificate | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { showToast } = useToast();

    const openDeleteModal = (certificate: Certificate) => {
        setItemToDelete(certificate);
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'certificates', itemToDelete.id));
            setCertificates(prev => prev.filter(c => c.id !== itemToDelete.id));
            showToast('Certificate deleted successfully.', { type: 'success' });
        } catch (error) {
            showToast("Error: Could not delete the certificate.", { type: 'error' });
        } finally {
            setIsModalOpen(false);
            setItemToDelete(null);
            setIsDeleting(false);
        }
    };
    
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (certificates && over && active.id !== over.id) {
            const oldIndex = certificates.findIndex((p) => p.id === active.id);
            const newIndex = certificates.findIndex((p) => p.id === over.id);
            const newOrder = arrayMove(certificates, oldIndex, newIndex);
            setCertificates(newOrder);

            try {
                const batch = writeBatch(db);
                newOrder.forEach((item, index) => {
                    batch.update(doc(db, 'certificates', item.id), { order: index });
                });
                await batch.commit();
                showToast('Certificate order updated.', { type: 'success' });
            } catch (error) {
                showToast('Failed to update certificate order.', { type: 'error' });
                setCertificates(certificates); // Revert on failure
            }
        }
    };

    return (
        <div>
            <ConfirmModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Certificate"
                description={`Are you sure you want to delete the certificate "${itemToDelete?.title}"? This action cannot be undone.`}
                isConfirming={isDeleting}
            />
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">Manage Certificates</h2>
                <Link to="/admin/certificate/new" className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">
                   <PlusIcon className="h-5 w-5 mr-2"/>
                    Add New Certificate
                </Link>
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                {isLoading && <p className="p-6 text-center">Loading certificates...</p>}
                {!isLoading && (
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 w-12"></th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Image</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Issuing Body</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Year</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={certificates?.map(s => s.id) || []} strategy={verticalListSortingStrategy}>
                                <tbody>
                                    {(certificates || []).map(certificate => (
                                        <SortableCertificateItem key={certificate.id} certificate={certificate} onDelete={openDeleteModal} />
                                    ))}
                                </tbody>
                            </SortableContext>
                        </DndContext>
                    </table>
                )}
                {!isLoading && certificates?.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <p>No certificates found.</p>
                        <p className="mt-2"><Link to="/admin/certificate/new" className="text-indigo-600 hover:underline">Add your first certificate!</Link></p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminCertificatesPage;